import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import { translate } from '../../utils/i18nHelper';
import {
  API_TENANTS_LIST,
  API_TENANTS_CREATE,
  API_TENANTS_GET,
  API_TENANTS_UPDATE,
  API_TENANTS_DELETE,
  API_TENANTS_GET_USERS,
  API_TENANTS_ASSIGN_USER,
  API_TENANTS_REMOVE_USER,
  API_TENANTS_GET_SUBSCRIPTIONS,
  API_TENANTS_SUBSCRIBE,
  API_TENANTS_UNSUBSCRIBE,
} from '../../constants/apiConstants';

export interface Tenant {
  id: number;
  guid: string;
  tenantCode: string;
  tenantName: string;
  contactEmail?: string;
  contactPhone?: string;
  billingPreference?: string;
  classification: string;
  type: string;
  status: string;
  isActive: boolean;
  isExpired?: boolean;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  configurationJson?: string;
  userCount?: number;
  createdAt?: string;
  createdBy?: string;
  modifiedAt?: string;
  modifiedBy?: string;
}

export interface TenantCreateRequest {
  tenantCode: string;
  tenantName: string;
  contactEmail?: string;
  contactPhone?: string;
  billingPreference?: string;
  classification?: string;
  type?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
}

export interface TenantUpdateRequest {
  tenantName?: string;
  contactEmail?: string;
  contactPhone?: string;
  billingPreference?: string;
  classification?: string;
  type?: string;
  subscriptionEndDate?: string;
}

export interface TenantAssignUserRequest {
  userId: number;
  roleCode: string;
}

export interface TenantSubscription {
  id: number;
  guid: string;
  tenantId: number;
  resourceName: string;
  resourceType: string;
  appType: string;  // "public" or "private"
  subscriptionType: string;
  billingModel: string;
  monthlyCost: number | null;
  perUsageCost: number | null;
  usageLimit: number | null;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  status: string;
  createdAt: string | null;
  modifiedAt: string | null;
}

export interface TenantSubscribeRequest {
  resourceName: string;
  resourceType: string;
  subscriptionType?: string;
  billingModel?: string;
  monthlyCost?: number | null;
  perUsageCost?: number | null;
  usageLimit?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface TenantUser {
  assignmentId: number;
  userId: number;
  userName: string;
  displayName: string;
  email?: string;
  roleCode: string;
  roleName: string;
  assignedDate?: string;
  isActive: boolean;
}

export const tenantService = {
  async getTenants(includeUserCount = false, userId?: number): Promise<Tenant[]> {
    try {
      const response = await apiClient.get(API_TENANTS_LIST, {
        params: { includeUserCount, ...(userId !== undefined ? { userId } : {}) },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.tenants ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch tenants', error);
      toast.error(`Failed to load tenants: ${msg}`);
      throw error;
    }
  },

  /**
   * Same as getTenants but also returns forceWorkspaceHome flag (strictest-wins across tenants).
   * Used by fetchUserTenants to support white-label mode.
   */
  async getTenantsWithConfig(userId: number): Promise<{ tenants: Tenant[]; forceWorkspaceHome: boolean }> {
    try {
      const response = await apiClient.get(API_TENANTS_LIST, {
        params: { includeUserCount: false, userId },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return {
        tenants: response.data.data?.tenants ?? [],
        forceWorkspaceHome: response.data.data?.forceWorkspaceHome ?? false,
      };
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch tenants with config', error);
      throw new Error(msg);
    }
  },

  async getTenant(id: number): Promise<Tenant> {
    try {
      const response = await apiClient.get(API_TENANTS_GET(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.tenant;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch tenant', error, { id });
      toast.error(`Failed to load tenant: ${msg}`);
      throw error;
    }
  },

  async createTenant(request: TenantCreateRequest): Promise<{ tenantId: number; tenantCode: string }> {
    try {
      const response = await apiClient.post(API_TENANTS_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Tenant created successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to create tenant', error);
      toast.error(`Failed to create tenant: ${msg}`);
      throw error;
    }
  },

  async updateTenant(id: number, request: TenantUpdateRequest): Promise<void> {
    try {
      const response = await apiClient.put(API_TENANTS_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Tenant updated successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to update tenant', error, { id });
      toast.error(`Failed to update tenant: ${msg}`);
      throw error;
    }
  },

  async deleteTenant(id: number): Promise<void> {
    try {
      const response = await apiClient.delete(API_TENANTS_DELETE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Tenant deleted successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to delete tenant', error, { id });
      toast.error(`Failed to delete tenant: ${msg}`);
      throw error;
    }
  },

  async getTenantUsers(tenantId: number): Promise<TenantUser[]> {
    try {
      const response = await apiClient.get(API_TENANTS_GET_USERS(tenantId));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.users ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch tenant users', error, { tenantId });
      toast.error(`Failed to load tenant users: ${msg}`);
      throw error;
    }
  },

  async assignUser(tenantId: number, request: TenantAssignUserRequest): Promise<void> {
    try {
      const response = await apiClient.post(API_TENANTS_ASSIGN_USER(tenantId), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('User assigned to tenant successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to assign user to tenant', error, { tenantId });
      toast.error(`Failed to assign user: ${msg}`);
      throw error;
    }
  },

  async removeUser(tenantId: number, userId: number): Promise<void> {
    try {
      const response = await apiClient.delete(API_TENANTS_REMOVE_USER(tenantId, userId));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('User removed from tenant successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to remove user from tenant', error, { tenantId, userId });
      toast.error(`Failed to remove user: ${msg}`);
      throw error;
    }
  },

  async getSubscriptions(tenantId: number, activeOnly = false): Promise<TenantSubscription[]> {
    try {
      const response = await apiClient.get(API_TENANTS_GET_SUBSCRIPTIONS(tenantId), { params: { activeOnly } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.subscriptions ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch tenant subscriptions', error, { tenantId });
      toast.error(`Failed to load subscriptions: ${msg}`);
      throw error;
    }
  },

  async subscribe(tenantId: number, request: TenantSubscribeRequest): Promise<{ subscriptionId: number }> {
    try {
      const response = await apiClient.post(API_TENANTS_SUBSCRIBE(tenantId), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success(translate('admin.tenantManagement.subscriptions.success.subscribed'));
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to subscribe tenant', error, { tenantId });
      toast.error(`${translate('admin.tenantManagement.subscriptions.error.subscribeFailed')}: ${msg}`);
      throw error;
    }
  },

  async unsubscribe(tenantId: number, subscriptionId: number): Promise<void> {
    try {
      const response = await apiClient.delete(API_TENANTS_UNSUBSCRIBE(tenantId, subscriptionId));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success(translate('admin.tenantManagement.subscriptions.success.unsubscribed'));
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to unsubscribe tenant', error, { tenantId, subscriptionId });
      toast.error(`${translate('admin.tenantManagement.subscriptions.error.unsubscribeFailed')}: ${msg}`);
      throw error;
    }
  },
};
